import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Form, Spinner, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Check, CreditCard, Lock } from 'react-feather';
import { getUserProfile, getSubscriptionPlans, createPayment } from '../services/api';
import SubscriptionCard from '../components/SubscriptionCard';
import { updateUserData } from '../services/auth';
import {Link} from "react-router-dom";

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: string;
  features: string[];
  highlighted?: boolean;
  eventsPerMonth: number | string;
}

const Subscription: React.FC = () => {
  const { t } = useTranslation();
  const [currentPlan, setCurrentPlan] = useState<string>('free');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState({
    cardName: '',
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  });
  const [paymentErrors, setPaymentErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get user profile to determine current subscription
      const userResponse = await getUserProfile();
      setCurrentPlan(userResponse.data.subscription_status);
      
      // Get available plans
      const plansResponse = await getSubscriptionPlans();
      setPlans(plansResponse.data.plans);
      
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load subscription data');
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (planId: string) => {
    const plan = plans.find(p => p.id === planId);
    if (plan && plan.id !== currentPlan) {
      setSelectedPlan(plan);
      setShowPaymentModal(true);
    }
  };

  const handlePaymentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPaymentInfo(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field if exists
    if (paymentErrors[name]) {
      setPaymentErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validatePaymentForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!paymentInfo.cardName.trim()) {
      errors.cardName = t('subscription.payment.errors.nameRequired');
    }
    
    if (!paymentInfo.cardNumber.trim()) {
      errors.cardNumber = t('subscription.payment.errors.numberRequired');
    } else if (!/^\d{16}$/.test(paymentInfo.cardNumber.replace(/\s/g, ''))) {
      errors.cardNumber = t('subscription.payment.errors.invalidNumber');
    }
    
    if (!paymentInfo.expiryDate.trim()) {
      errors.expiryDate = t('subscription.payment.errors.expiryRequired');
    } else if (!/^\d{2}\/\d{2}$/.test(paymentInfo.expiryDate)) {
      errors.expiryDate = t('subscription.payment.errors.invalidExpiry');
    }
    
    if (!paymentInfo.cvv.trim()) {
      errors.cvv = t('subscription.payment.errors.cvvRequired');
    } else if (!/^\d{3,4}$/.test(paymentInfo.cvv)) {
      errors.cvv = t('subscription.payment.errors.invalidCvv');
    }
    
    setPaymentErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedPlan) return;
    if (!validatePaymentForm()) return;
    
    try {
      setProcessingPayment(true);
      
      // Create payment for subscription
      const paymentData = {
        amount: selectedPlan.price,
        payment_type: 'subscription',
        subscription_tier: selectedPlan.id,
        payment_method: 'credit_card',
        transaction_id: `sub_${Date.now()}` // This would normally come from a payment processor
      };
      
      await createPayment(paymentData);
      
      // Update local user data
      updateUserData({ subscription_status: selectedPlan.id });
      
      // Close payment modal and show confirmation
      setShowPaymentModal(false);
      setShowConfirmationModal(true);
      setCurrentPlan(selectedPlan.id);
      
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to process payment');
      console.error('Error processing payment:', error);
    } finally {
      setProcessingPayment(false);
    }
  };

  const renderFeatureComparison = () => {
    // Define all possible features
    const allFeatures = [
      'subscription.features.eventsPerMonth',
      'subscription.features.analytics',
      'subscription.features.support',
      'subscription.features.verifiedBadge',
      'subscription.features.customBranding'
    ];
    
    // Map features to each plan
    const planFeatures: {[key: string]: string[]} = {
      free: [
        'subscription.features.eventsPerMonth',
        'subscription.features.analytics',
        'subscription.features.support'
      ],
      premium: allFeatures
    };
    
    // Feature details by plan
    const featureDetails: {[key: string]: {[key: string]: any}} = {
      free: {
        'subscription.features.eventsPerMonth': '7',
        'subscription.features.analytics': t('subscription.features.basicAnalytics'),
        'subscription.features.support': t('subscription.features.standardSupport')
      },
      premium: {
        'subscription.features.eventsPerMonth': t('subscription.features.unlimited'),
        'subscription.features.analytics': t('subscription.features.advancedAnalytics'),
        'subscription.features.support': t('subscription.features.prioritySupport'),
        'subscription.features.verifiedBadge': t('subscription.features.includedVerifiedBadge'),
        'subscription.features.customBranding': t('subscription.features.includedCustomBranding')
      }
    };
    
    return (
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>{t('subscription.features.feature')}</th>
              {plans.map(plan => (
                <th key={plan.id} className="text-center">
                  {plan.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allFeatures.map(feature => (
              <tr key={feature}>
                <td>{t(feature)}</td>
                {plans.map(plan => (
                  <td key={`${plan.id}-${feature}`} className="text-center">
                    {planFeatures[plan.id].includes(feature) ? (
                      <>
                        <Check className="text-success me-1" size={18} />
                        {featureDetails[plan.id][feature]}
                      </>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">{t('loading')}</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="mb-1">{t('subscription.title')}</h1>
          <p className="text-muted">{t('subscription.subtitle')}</p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <Row className="justify-content-center g-4">
        <Col md={6} lg={4}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="p-4 text-center">
              <Card.Title className="fw-bold">{t('home.pricing.free.title')}</Card.Title>
              <div className="price-tag my-4">
                <span className="h1 fw-bold">€0</span>
                <span className="text-muted">/{t('home.pricing.month')}</span>
              </div>
              <ul className="list-unstyled text-start">
                <li className="mb-3 d-flex align-items-center">
                  <Check className="text-success me-2" size={20} />
                  {t('home.pricing.free.feature1')}
                </li>
                <li className="mb-3 d-flex align-items-center">
                  <Check className="text-success me-2" size={20} />
                  {t('home.pricing.free.feature2')}
                </li>
                <li className="mb-3 d-flex align-items-center">
                  <Check className="text-success me-2" size={20} />
                  {t('home.pricing.free.feature3')}
                </li>
              </ul>
              <div className="d-grid mt-4">
                <Button
                    as={Link as unknown as any}
                    to="/signup"
                    variant="outline-primary"
                >
                  {t('home.pricing.free.button')}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={4}>
          <Card className="h-100 shadow border-primary">
            <div className="bg-primary text-white text-center py-2">
              <span className="fw-bold">{t('home.pricing.popular')}</span>
            </div>
            <Card.Body className="p-4 text-center">
              <Card.Title className="fw-bold">{t('home.pricing.premium.title')}</Card.Title>
              <div className="price-tag my-4">
                <span className="h1 fw-bold">€5.99</span>
                <span className="text-muted">/{t('home.pricing.month')}</span>
              </div>
              <ul className="list-unstyled text-start">
                <li className="mb-3 d-flex align-items-center">
                  <Check className="text-success me-2" size={20} />
                  {t('home.pricing.premium.feature1')}
                </li>
                <li className="mb-3 d-flex align-items-center">
                  <Check className="text-success me-2" size={20} />
                  {t('home.pricing.premium.feature2')}
                </li>
                <li className="mb-3 d-flex align-items-center">
                  <Check className="text-success me-2" size={20} />
                  {t('home.pricing.premium.feature3')}
                </li>
                {/*<li className="mb-3 d-flex align-items-center">*/}
                {/*  <Check className="text-success me-2" size={20} />*/}
                {/*  {t('home.pricing.premium.feature4')}*/}
                {/*</li>*/}
              </ul>
              <div className="d-grid mt-4">
                <Button
                    as={Link as unknown as any}
                    to="/signup"
                    variant="primary"
                >
                  {t('home.pricing.premium.button')}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4}>
          <Card className="h-100 shadow border-primary">

            <Card.Body className="p-4 text-center">
              <Card.Title className="fw-bold">Expert</Card.Title>
              <div className="price-tag my-4">
                <span className="h1 fw-bold">€9.99</span>
                <span className="text-muted">/{t('home.pricing.month')}</span>
              </div>
              <ul className="list-unstyled text-start">
                <li className="mb-3 d-flex align-items-center">
                  <Check className="text-success me-2" size={20} />
                  {t('home.pricing.premium.feature1')}
                </li>
                <li className="mb-3 d-flex align-items-center">
                  <Check className="text-success me-2" size={20} />
                  {t('home.pricing.premium.feature2')}
                </li>
                <li className="mb-3 d-flex align-items-center">
                  <Check className="text-success me-2" size={20} />
                  {t('home.pricing.premium.feature3')}
                </li>
                <li className="mb-3 d-flex align-items-center">
                  <Check className="text-success me-2" size={20} />
                  {t('home.pricing.premium.feature4')}
                </li>
              </ul>
              <div className="d-grid mt-4">
                <Button
                    as={Link as unknown as any}
                    to="/signup"
                    variant="primary"
                >
                  {t('home.pricing.premium.button')}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/*<Row>*/}
      {/*  <Col>*/}
      {/*    <Card className="shadow-sm">*/}
      {/*      <Card.Header className="bg-white py-3">*/}
      {/*        <h5 className="mb-0">{t('subscription.features.title')}</h5>*/}
      {/*      </Card.Header>*/}
      {/*      <Card.Body>*/}
      {/*        {renderFeatureComparison()}*/}
      {/*      </Card.Body>*/}
      {/*    </Card>*/}
      {/*  </Col>*/}
      {/*</Row>*/}

      {/* Payment Modal */}
      <Modal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('subscription.payment.title')}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePaymentSubmit}>
          <Modal.Body>
            {selectedPlan && (
              <Alert variant="info">
                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    {t('subscription.payment.subscribingTo')}: <strong>{selectedPlan.name}</strong>
                  </span>
                  <span className="fw-bold">
                    {selectedPlan.currency}{selectedPlan.price}/{selectedPlan.interval}
                  </span>
                </div>
              </Alert>
            )}
            
            <div className="mb-4">
              <Form.Group className="mb-3">
                <Form.Label>{t('subscription.payment.cardName')}</Form.Label>
                <Form.Control
                  type="text"
                  name="cardName"
                  value={paymentInfo.cardName}
                  onChange={handlePaymentChange}
                  placeholder={t('subscription.payment.cardNamePlaceholder') as string}
                  isInvalid={!!paymentErrors.cardName}
                />
                <Form.Control.Feedback type="invalid">
                  {paymentErrors.cardName}
                </Form.Control.Feedback>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>{t('subscription.payment.cardNumber')}</Form.Label>
                <Form.Control
                  type="text"
                  name="cardNumber"
                  value={paymentInfo.cardNumber}
                  onChange={handlePaymentChange}
                  placeholder="1234 5678 9012 3456"
                  isInvalid={!!paymentErrors.cardNumber}
                />
                <Form.Control.Feedback type="invalid">
                  {paymentErrors.cardNumber}
                </Form.Control.Feedback>
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('subscription.payment.expiryDate')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="expiryDate"
                      value={paymentInfo.expiryDate}
                      onChange={handlePaymentChange}
                      placeholder="MM/YY"
                      isInvalid={!!paymentErrors.expiryDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {paymentErrors.expiryDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('subscription.payment.cvv')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="cvv"
                      value={paymentInfo.cvv}
                      onChange={handlePaymentChange}
                      placeholder="123"
                      isInvalid={!!paymentErrors.cvv}
                    />
                    <Form.Control.Feedback type="invalid">
                      {paymentErrors.cvv}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>
            
            <div className="d-flex align-items-center justify-content-center mb-2 text-muted">
              <Lock size={16} className="me-2" />
              <small>{t('subscription.payment.secureInfo')}</small>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowPaymentModal(false)}
              disabled={processingPayment}
            >
              {t('subscription.payment.cancelButton')}
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={processingPayment}
            >
              {processingPayment ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {t('subscription.payment.processingButton')}
                </>
              ) : (
                <div className="d-flex align-items-center">
                  <CreditCard size={16} className="me-2" />
                  {t('subscription.payment.submitButton')}
                </div>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('subscription.confirmation.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="confirmation-icon bg-success rounded-circle mx-auto mb-4 d-flex align-items-center justify-content-center">
            <Check size={40} color="white" />
          </div>
          <h4 className="mb-3">{t('subscription.confirmation.message')}</h4>
          <p className="mb-1">{t('subscription.confirmation.startDate')}</p>
          <p className="mb-4">
            {t('subscription.confirmation.nextBilling')}: {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
          </p>
          <Button 
            variant="primary" 
            onClick={() => {
              setShowConfirmationModal(false);
              window.location.href = '/dashboard';
            }}
          >
            {t('subscription.confirmation.goToDashboard')}
          </Button>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Subscription;
